import { yupResolver } from "@hookform/resolvers/yup";
import { useRouter } from "next/router";
import { useCallback, useEffect, useRef, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import * as yup from "yup";

import { useActiveClassroom } from "@sol/classrooms";
import { interpolate, Route } from "@sol/routing";
import { FileType, getResourceUuid, IUserResource } from "@sol/sdk";
import { useClassroomLearnersMutation } from "@sol/sdk/classrooms/mutations/classroomUpdateLearnersMutation";
import { message } from "@sol/uikit/feedback/Message/Message";

import { ClassroomLearnersFileImport, SUPPORTED_FORMATS } from "./ClassroomLearnersFileImport";
import { ClassroomLearnersFileImportModal } from "./ClassroomLearnersFileImportModal";
import { LearnerPicker } from "./LearnerPicker";
import ApiErrorAlert from "../../../components/ApiErrorAlert";
import MetaTitle from "../../../components/MetaTitle";

const schema = yup
    .object()
    .shape({
        learners: yup.array<IUserResource>(),
        fileType: yup.string(),
        file: yup
            .mixed<File>()
            .notRequired()
            .test("fileFormat", "Unsupported Format", value => {
                if (!value) {
                    return true;
                }
                // If the type is not reconized (windows) we check the file extension in the file name
                return value.type ? SUPPORTED_FORMATS.includes(value.type) : value.name.search(/\.csv+$/i) !== -1;
            }),
    })
    .required();

export type ClassroomLearnersFormValues = yup.InferType<typeof schema>;

export const ClassroomLearners = () => {
    const [t] = useTranslation();
    const { activeClassroom: classroom, refetch: refetchClassroom } = useActiveClassroom();
    const { push, query } = useRouter();

    const form = useForm<ClassroomLearnersFormValues>({
        resolver: yupResolver(schema),
        defaultValues: { learners: [], fileType: FileType.CSV },
    });

    const [isImport, setIsImport] = useState(false);
    const [displayImportModal, setDisplayModalImport] = useState(false);
    const [messageApi, contextHolder] = message.useMessage();

    const learners = form.watch("learners");

    useEffect(() => {
        form.register("learners");
        form.register("fileType");
        form.register("file");
    }, [form.register]);

    // Only seed the list once per classroom: a background refetch would otherwise wipe the learners
    // added but not saved yet
    const seededClassroomRef = useRef<string | null>(null);

    useEffect(() => {
        if (!classroom || seededClassroomRef.current === classroom["@id"]) {
            return;
        }

        seededClassroomRef.current = classroom["@id"];
        form.setValue("learners", classroom.learners);
    }, [classroom, form.setValue]);

    const handleBack = useCallback(() => {
        if (isImport) {
            form.clearErrors("file");
            form.setValue("fileType", FileType.CSV);
            form.setValue("file", undefined);
            setIsImport(false);
        } else {
            push(
                interpolate(Route.CLASSROOM_DASHBOARD, {
                    classroomId: classroom ? getResourceUuid(classroom) : query.classroomId,
                }),
            );
        }
    }, [form.clearErrors, form.setValue, setIsImport, push, isImport]);

    const {
        mutate: classroomLearnersMutation,
        error: classroomLearnersMutationError,
        reset: classroomLearnersMutationReset,
        isPending: classroomLearnersMutationIsSubmitting,
    } = useClassroomLearnersMutation({
        onSuccess: () => {
            messageApi.success(t("page.home.confirmMessages.updateLearners"));
            refetchClassroom();
            push(
                interpolate(Route.CLASSROOM_DASHBOARD, {
                    classroomId: classroom ? getResourceUuid(classroom) : query.classroomId,
                }),
            );
        },
    });

    return (
        <>
            <MetaTitle title={t("page.classrooms.learners.meta.title", { classroomName: classroom?.title })} />
            <div className="rounded-ant-md flex justify-center bg-white p-ant-lg">
                {isImport ? (
                    <>
                        <FormProvider {...form}>
                            <ClassroomLearnersFileImport
                                className="w-full"
                                onCancel={() => setIsImport(false)}
                                onSubmit={form.handleSubmit(() => {
                                    setDisplayModalImport(true);
                                })}
                            />
                            <ClassroomLearnersFileImportModal
                                isVisible={displayImportModal}
                                onCancel={() => setDisplayModalImport(false)}
                            />
                        </FormProvider>
                        <ApiErrorAlert
                            error={classroomLearnersMutationError}
                            dissmiss={classroomLearnersMutationReset}
                        />
                    </>
                ) : (
                    <LearnerPicker
                        className="w-full"
                        learners={learners}
                        onImportClick={() => setIsImport(true)}
                        onSubmit={learners => classroom && classroomLearnersMutation({ classroom, learners })}
                        onCancel={handleBack}
                        isSubmitting={classroomLearnersMutationIsSubmitting}
                        apiErrorsReset={classroomLearnersMutationReset}
                        apiErrors={classroomLearnersMutationError}
                    />
                )}
            </div>
            {contextHolder}
        </>
    );
};
